import React from 'react';

import classNames from 'classnames';

import type { IconLookup } from '@fortawesome/fontawesome-svg-core';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useTextField } from '@react-aria/textfield';

import type { IFieldProps } from 'bz-core-controls/controls/form/field/field/IFieldProps';

import { HoverInfo } from 'click-controls/controls/generics/hover-info';
import type { IProps as IToolTipProps } from 'click-controls/controls/generics/hover-info';

export interface IToolTip extends Pick<IToolTipProps, 'latchable'> {
  tooltipActiveTooltipBottomCSS?: IToolTipProps['activeTooltipBottomCSS'];
  tooltipAlignTriangle?: IToolTipProps['alignTriangle'];
  tooltipContent?: IToolTipProps['content'];
  tooltipLatchable?: IToolTipProps['latchable'];
  tooltipMarginBottom?: IToolTipProps['marginBottom'];
  tooltipPosition?: IToolTipProps['position'];
  tooltipTranform?: IToolTipProps['transform'];
  tooltipTranformTriangle?: IToolTipProps['transformTriangle'];
  tooltipTitle?: IToolTipProps['title'];
}

export interface ITextAreaAriaProps extends IFieldProps, IToolTip {
  description: React.ReactNode;
  errorText?: string;
  errorTextClassName?: string;
  hasTooltip?: boolean;
  labelClass?: string;
  required?: boolean;
}

const TextAreaAria: React.FC<ITextAreaAriaProps> = (props: ITextAreaAriaProps) => {
  const { label, errorText } = props;

  const ref = React.useRef(null);

  const { labelProps, inputProps, descriptionProps, errorMessageProps } = useTextField(props, ref);

  const errorArray = React.useMemo(() => {
    return errorText?.split(',') ?? [];
  }, [errorText]);

  return (
    <div className="">
      <label {...labelProps} className={classNames('click-label', props.labelClass)}>
        <span>
          {label}
          {props.required && <span className="text-normal pl-px text-rose-600">*</span>}
        </span>
        {props.hasTooltip && (
          <HoverInfo
            activeTooltipTopCSS={props.tooltipActiveTooltipBottomCSS}
            alignTriangle={props.tooltipAlignTriangle}
            content={props.tooltipContent}
            icon={faInfoCircle as IconLookup}
            iconClass="text-amber-400 text-lg"
            latchable={props.tooltipLatchable}
            marginBottom={props.tooltipMarginBottom}
            position={'TOP'}
            title={props.tooltipTitle}
          >
            <FontAwesomeIcon
              className="mx-2 cursor-pointer text-lg text-amber-400"
              icon={faInfoCircle as IconLookup}
            />
          </HoverInfo>
        )}
      </label>
      <textarea {...inputProps} ref={ref} className={props.className} disabled={props.disabled} />
      {!!props.description && (
        <div {...descriptionProps} className="text-xs font-medium text-gray-300">
          {props.description}
        </div>
      )}
      {!!props.errorText && props.submitted && (
        <div
          className={classNames(
            'mt-1 pb-2 text-xs font-medium text-rose-500',
            props.errorTextClassName
          )}
        >
          <p>{errorArray.length && errorArray[errorArray.length - 1]}</p>
        </div>
      )}
    </div>
  );
};

export default class TextArea extends React.Component<ITextAreaAriaProps> {
  constructor(props: ITextAreaAriaProps) {
    super(props);
  }

  render() {
    return <TextAreaAria {...this.props} />;
  }
}
